import { Component, OnInit, OnDestroy, Input, HostListener } from '@angular/core';
import { AuthenticationService } from '../../../Services/authentication/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../Services/modal/modal.service';
import { AnalyticsService } from '../../../Services/analytics/analytics.service';
import { Router, ActivatedRoute } from '@angular/router';
import { EmojiService } from '../../../Services/emoji/emoji.service';
import { MixpanelService } from '../../../Services/mixpanel/mixpanel.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-auth-modal',
  templateUrl: './auth-modal.component.html',
  styleUrls: ['./auth-modal.component.scss']
})
export class AuthModalComponent implements OnInit, OnDestroy {

  public currentScreen = 'registration';
  public showRegistrationForm = false;
  public showLoginForm = false;
  public showConsent = false;
  public generalConsentStatus = false;
  public nameField;
  public emailField;
  public passwordField;
  private regStep;
  public nameError = false;
  public passwordError = false;
  public emailError = false;
  private registrationSubscriber;
  private loginSubscriber;
  private passwordRecoverySubscriber;
  public error = false;
  public errorContent;
  @Input() returnURL;
  @Input() queryParams;
  @Input() closeURL;
  public success = false;
  public loading = false;
  public loginEmoji;
  public regEmoji;
  public forgotPassEmoji;
  public forgotPassSuccessEmoji;

  constructor(
    private auth: AuthenticationService,
    public translate: TranslateService,
    private modalService: ModalService,
    private analytics: AnalyticsService,
    private route: ActivatedRoute,
    private router: Router,
    private emoji: EmojiService,
    private mixpanel: MixpanelService
  ) { }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      switch (this.currentScreen) {
        case 'registration':
          if (this.showRegistrationForm && !this.showConsent && !this.success) {
            this.register();
          }
          break;
        case 'login':
          if (this.showLoginForm && !this.success) {
            this.login();
          }
          break;
        case 'forgotPass':
          if (!this.success) {
            this.recoverPassword();
          }
          break;
        default:
          break;
      }
    }
  }

  ngOnInit() {
    this.currentScreen = 'registration';
    this.returnURL = this.returnURL ? this.returnURL : this.route.snapshot.queryParams['returnUrl'];
    this.loginEmoji = this.emoji.getEmoji('👋');
    this.regEmoji = this.emoji.getEmoji('✨');
    this.forgotPassEmoji = this.emoji.getEmoji('👀');
    this.forgotPassSuccessEmoji = this.emoji.getEmoji('👍');
  }

  mainSiteURL() {
    return environment.metaUrl;
  }

  changeScreen(newScreen) {
    this.currentScreen = newScreen;
    this.showConsent = false;
    this.showRegistrationForm = false;
    this.showLoginForm = false;
    this.error = false;
    this.success = false;
  }

  fbBehavior() {
    if (this.currentScreen === 'registration') {
      if (!this.generalConsentStatus) {
        this.showConsent = true;
        this.regStep = 'facebook';
        return;
      }
    }
    const formValue = this.collectFormValues();
    this.auth.fbLogin(formValue, null,
      this.socialSuccess.bind(this), this.failRequest.bind(this));
  }

  gBehavior() {
    if (this.currentScreen === 'registration') {
      if (!this.generalConsentStatus) {
        this.showConsent = true;
        this.regStep = 'google';
        return;
      }
    }
    const formValue = this.collectFormValues();
    this.auth.gLogin(formValue, null,
      this.socialSuccess.bind(this), this.failRequest.bind(this));
  }

  mailBehavior() {
    if (this.currentScreen === 'registration') {
      if (!this.generalConsentStatus) {
        this.showConsent = true;
        this.regStep = 'email';
        return;
      }
      this.showRegistrationForm = true;
    } else if (this.currentScreen === 'login') {
      this.showLoginForm = true;
    }
  }

  handleConsentAgree(event) {
    this.generalConsentStatus = !this.generalConsentStatus;
    if (this.generalConsentStatus) {
      if (this.regStep === 'facebook') {
        this.fbBehavior();
      } else if (this.regStep === 'google') {
        this.gBehavior();
      } else if (this.regStep === 'email') {
        this.mailBehavior();
      }
      this.showConsent = false;
    }
  }

  socialSuccess(response) {
    this.success = true;
    this.loading = false;
    this.error = false;
    if (!response.user_created) {
        const user = JSON.parse(localStorage.getItem('user'));
        this.mixpanel.identify(user.id);
        this.mixpanel.peopleSet(user.id, {
            "$email": user.email,
            "$first_name": user.profile.first_name ? user.profile.first_name : "",
            "$last_name": user.profile.last_name ? user.profile.last_name: "",
        });
      if (response.service === 'google') {
        this.analytics.logEvent('login_with_google');
        this.mixpanel.track('sign_in_with_google');
      } else if (response.service === 'facebook') {
        this.analytics.logEvent('login_with_facebook');
        this.mixpanel.track('sign_in_with_facebook');
      }
    } else if (response.user_created) {
      const user = JSON.parse(localStorage.getItem('user'));
      this.mixpanel.alias(user.id);
      this.mixpanel.peopleSet(user.id, {
          "$email": user.email,
          "$first_name": user.profile.first_name ? user.profile.first_name : "",
          "$last_name": user.profile.last_name ? user.profile.last_name: "",
      });
      if (response.service === 'google') {
        this.analytics.logEvent('sign_up_with_google');
        this.mixpanel.track('sign_up_with_google');
      } else if (response.service === 'facebook') {
        this.analytics.logEvent('sign_up_with_facebook');
        this.mixpanel.track('sign_up_with_facebook');
      }
    }
    window.setTimeout(function () {
      try {
        this.removeModal();
      } catch (error) {
        console.warn(error);
      }
    }.bind(this), 400);
    if (this.returnURL) {
      this.router.navigate([this.returnURL], { queryParams: this.queryParams });
    }
  }

  failRequest(response) {
    this.error = true;
    this.loading = false;
    this.errorContent = response.error.error;
    if (response.error.error_details) {
      for (const error in response.error.error_details) {
        if (response.error.error_details.hasOwnProperty(error)) {
          const error_Element = response.error.error_details[error];
          error_Element.forEach(errorItem => {
            this.errorContent += '\n';
            this.errorContent += errorItem;
          });
        }
      }
    }
  }

  private collectFormValues() {
    return {
      marketingConsent: this.generalConsentStatus,
      termsConsent: this.generalConsentStatus,
      email: this.emailField,
      name: this.nameField,
      password: this.passwordField,
    };
  }


  register() {
    if (this.loading) {
      return;
    }
    this.loading = true;
    const validationResult = this.validateFormValue('registration');
    if (validationResult) {
      const formValue = this.collectFormValues();
      this.registrationSubscriber = this.auth.registration(formValue).subscribe(
        resp => {
          this.success = true;
          this.error = false;
          this.loading = false;
          this.analytics.logEvent('sign_up_with_email');

          const user = JSON.parse(localStorage.getItem('user'));
          this.mixpanel.alias(user.id);
          this.mixpanel.peopleSet(user.id, {
              "$email": user.email,
              "$first_name": user.profile.first_name ? user.profile.first_name : "",
              "$last_name": user.profile.last_name ? user.profile.last_name: "",
          });

          this.mixpanel.track('sign_up_with_email', {
              email: user.email,
              first_name: user.profile.first_name,
              last_name: user.profile.last_name,
          });

          window.setTimeout(function () {
            try {
              this.removeModal();
            } catch (error) {
              console.warn(error);
            }
          }.bind(this), 400);
          if (this.returnURL) {
            this.router.navigate([this.returnURL], { queryParams: this.queryParams });
          }
        },
        err => {
          this.failRequest(err);
        }
      );
    } else {
      this.loading = false;
    }
  }

  login() {
    if (this.loading) {
      return;
    }
    this.loading = true;
    const validationResult = this.validateFormValue('login');
    if (validationResult) {
      const formValue = this.collectFormValues();
      this.loginSubscriber = this.auth.login(formValue).subscribe(
        resp => {
          this.success = true;
          this.error = false;
          this.loading = false;
          this.analytics.logEvent('login_with_email');

          const user = JSON.parse(localStorage.getItem('user'));
          this.mixpanel.identify(user.id);

          this.mixpanel.peopleSet(user.id, {
              "$email": user.email,
              "$first_name": user.profile.first_name ? user.profile.first_name : "",
              "$last_name": user.profile.last_name ? user.profile.last_name: "",
          });

          this.mixpanel.track('sign_in_with_email', {
              email: user.email,
              first_name: user.profile.first_name,
              last_name: user.profile.last_name,
          });

          window.setTimeout(function () {
            try {
              this.removeModal();
            } catch (error) {
              console.warn(error);
            }
          }.bind(this), 400);
          if (this.returnURL) {
            this.router.navigate([this.returnURL], { queryParams: this.queryParams });
          }
        },
        err => {
          this.failRequest(err);
        }
      );
    } else {
      this.loading = false;
    }
  }

  recoverPassword() {
    if (this.loading) {
      return;
    }
    this.loading = true;
    const validationResult = this.validateFormValue('forgotPass');
    if (validationResult) {
      const formValue = this.collectFormValues();
      this.passwordRecoverySubscriber = this.auth.passwordRecovery(formValue).subscribe(
        resp => {
          this.success = true;
          this.error = false;
          this.loading = false;
          this.analytics.logEvent('password_reset_requested');
          window.setTimeout(function () {
            try {
              this.removeModal();
            } catch (error) {
              console.warn(error);
            }
          }.bind(this), 400);
        },
        err => {
          this.failRequest(err);
        }
      );
    } else {
      this.loading = false;
    }
  }

  private validateFormValue(validationType) {
    this.emailError = false;
    this.nameError = false;
    this.passwordError = false;
    if (validationType === 'registration') {
      return this.validateRegistrationForm();
    } else if (validationType === 'login') {
      return this.validateLoginForm();
    } else if (validationType === 'forgotPass') {
      return this.validateForgotPass();
    }
  }

  private validateRegistrationForm() {
    const formValue = this.collectFormValues();
    if (formValue.email) {
      this.emailError = !this.validateEmail(formValue.email);
    } else {
      this.emailError = true;
    }
    if (!formValue.name) {
      this.nameError = true;
    }
    if (formValue.password) {
      if (formValue.password.length < 6) {
        this.passwordError = true;
      }
    } else {
      this.passwordError = true;
    }
    return !this.passwordError && !this.nameError && !this.emailError;
  }

  private validateLoginForm() {
    const formValue = this.collectFormValues();
    if (formValue.email) {
      this.emailError = !this.validateEmail(formValue.email);
    } else {
      this.emailError = true;
    }
    if (formValue.password) {
      if (formValue.password.length < 6) {
        this.passwordError = true;
      }
    } else {
      this.passwordError = true;
    }
    return !this.passwordError && !this.emailError;
  }

  private validateForgotPass() {
    const formValue = this.collectFormValues();
    if (formValue.email) {
      this.emailError = !this.validateEmail(formValue.email);
    } else {
      this.emailError = true;
    }
    return !this.emailError;
  }

  private validateEmail(email) {
    /* tslint:disable */
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    /* tslint:enable */
    return re.test(email);
  }

  ngOnDestroy() {
    if (this.registrationSubscriber) {
      this.registrationSubscriber.unsubscribe();
    }
    if (this.loginSubscriber) {
      this.loginSubscriber.unsubscribe();
    }
    if (this.passwordRecoverySubscriber) {
      this.passwordRecoverySubscriber.unsubscribe();
    }
  }

  removeModal() {
    if (this.closeURL) {
      this.router.navigate([this.closeURL]);
    }
    this.modalService.destroy();
  }

}
